// Regras puras da tela de follow-ups (testadas em regras.test.ts). A conta do
// ciclo mora no servidor (handlers/followups.js → `situacao`); aqui só o que a
// tela decide sozinha: rótulos, cores, a frase da regra e o total da limpeza.
import type { FollowupConfig, ItemFila, ItemLimpeza, Modo, ResumoRodada } from './api';

export const ROTULO_MODO: Record<Modo, string> = {
  desligado: 'Desligado',
  manual: 'Tarefa (manual)',
  automatico: 'E-mail automático',
};

export const DIAS_SEMANA = [
  { valor: 1, curto: 'seg' }, { valor: 2, curto: 'ter' }, { valor: 3, curto: 'qua' },
  { valor: 4, curto: 'qui' }, { valor: 5, curto: 'sex' }, { valor: 6, curto: 'sáb' }, { valor: 0, curto: 'dom' },
];

/** Cor da etiqueta da situação (códigos de `situacao` no servidor). */
export const COR_SITUACAO: Record<string, string> = {
  devido: 'orange',
  tarefa_aberta: 'blue',
  agendado: 'default',
  aguardando_resposta: 'purple',
  pronto_para_perder: 'red',
  pausado: 'cyan',
  desligado: 'default',
  sem_data: 'gold',
  fora: 'default',
};

/** Ordem da fila: o que pede ação primeiro. */
export const ORDEM_SITUACAO = ['pronto_para_perder', 'devido', 'tarefa_aberta', 'sem_data', 'aguardando_resposta', 'agendado', 'pausado', 'desligado', 'fora'];

export function ordenarFila(itens: ItemFila[]): ItemFila[] {
  const pos = (c: string) => { const i = ORDEM_SITUACAO.indexOf(c); return i < 0 ? 99 : i; };
  return [...itens].sort((a, b) => pos(a.situacao.codigo) - pos(b.situacao.codigo)
    || String(a.situacao.data || a.proximo_em || '9999').localeCompare(String(b.situacao.data || b.proximo_em || '9999'))
    || a.id - b.id);
}

export function contarPorSituacao(itens: ItemFila[]): Record<string, number> {
  const r: Record<string, number> = {};
  for (const i of itens) r[i.situacao.codigo] = (r[i.situacao.codigo] || 0) + 1;
  return r;
}

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

/** A regra em uma frase — o que aparece no topo da fila e na aba Regras. */
export function fraseDaCadencia(c: Pick<FollowupConfig, 'primeiro_apos_dias' | 'intervalo_dias' | 'max_tentativas' | 'perda_automatica' | 'perder_apos_dias'>): string {
  const base = c.max_tentativas === 1
    ? `Um follow-up ${plural(c.primeiro_apos_dias, 'dia', 'dias')} depois de o negócio entrar em Follow-up`
    : `1º follow-up ${plural(c.primeiro_apos_dias, 'dia', 'dias')} depois de o negócio entrar em Follow-up, `
      + `depois a cada ${plural(c.intervalo_dias, 'dia', 'dias')}, até ${c.max_tentativas}`;
  const fim = c.perda_automatica
    ? `sem resposta ${plural(c.perder_apos_dias, 'dia', 'dias')} depois do último, o negócio vira perdido ("Sem retorno do cliente")`
    : `sem resposta depois do último, o negócio fica marcado para você decidir (a perda automática está desligada)`;
  return `${base}; ${fim}.`;
}

export function fraseDaJanela(c: Pick<FollowupConfig, 'janela_dias_semana' | 'janela_hora_inicio' | 'janela_hora_fim'>): string {
  const dias = DIAS_SEMANA.filter(d => (c.janela_dias_semana || []).includes(d.valor)).map(d => d.curto);
  const seguidos = dias.join(', ') === 'seg, ter, qua, qui, sex' ? 'dias úteis' : dias.join(', ');
  return `${seguidos || 'nenhum dia'}, das ${c.janela_hora_inicio}h às ${c.janela_hora_fim}h (Brasília)`;
}

/** O que a rodada fez (ou, no ensaio, FARIA), em uma linha. */
export function fraseDaRodada(r: ResumoRodada | null | undefined, ensaio = false): string {
  if (!r) return 'ainda não rodou';
  const partes = (ensaio ? [
    r.enviados ? plural(r.enviados, 'e-mail a enviar', 'e-mails a enviar') : null,
    r.tarefas ? plural(r.tarefas, 'tarefa a criar', 'tarefas a criar') : null,
    r.contadas ? plural(r.contadas, 'tarefa concluída a contar', 'tarefas concluídas a contar') : null,
    r.perdidos ? plural(r.perdidos, 'negócio a perder', 'negócios a perder') : null,
    r.avisos_perda ? plural(r.avisos_perda, 'aviso de perda a mandar', 'avisos de perda a mandar') : null,
  ] : [
    r.enviados ? plural(r.enviados, 'e-mail enviado', 'e-mails enviados') : null,
    r.tarefas ? plural(r.tarefas, 'tarefa criada', 'tarefas criadas') : null,
    r.contadas ? plural(r.contadas, 'tarefa concluída contada', 'tarefas concluídas contadas') : null,
    r.perdidos ? plural(r.perdidos, 'negócio perdido', 'negócios perdidos') : null,
    r.avisos_perda ? plural(r.avisos_perda, 'aviso de perda', 'avisos de perda') : null,
    r.falhas ? plural(r.falhas, 'e-mail que NÃO saiu', 'e-mails que NÃO saíram') : null,
    r.incertos ? plural(r.incertos, 'e-mail incerto (virou tarefa para conferir)', 'e-mails incertos (viraram tarefa para conferir)') : null,
  ]).filter(Boolean);
  return partes.length ? partes.join(' · ') : 'nada a fazer';
}

/** A janela que vai ficar salva é válida? (a tela manda só o que mudou). */
export const janelaValida = (inicio: number, fim: number) => Number(fim) > Number(inicio);

/** Insere `{{chave}}` onde está o cursor (ou no fim). */
export function inserirCampo(texto: string, chave: string, inicio?: number | null, fim?: number | null): { texto: string; cursor: number } {
  const t = texto || '';
  const a = inicio == null || inicio < 0 || inicio > t.length ? t.length : inicio;
  const b = fim == null || fim < a || fim > t.length ? a : fim;
  const campo = `{{${chave}}}`;
  return { texto: t.slice(0, a) + campo + t.slice(b), cursor: a + campo.length };
}

export type AcaoLimpeza = 'perder' | Modo;

export function totaisDaLimpeza(itens: ItemLimpeza[], escolha: Record<number, AcaoLimpeza>) {
  const r: Record<AcaoLimpeza, { n: number; valor: number }> = {
    perder: { n: 0, valor: 0 }, manual: { n: 0, valor: 0 }, automatico: { n: 0, valor: 0 }, desligado: { n: 0, valor: 0 },
  };
  for (const i of itens) {
    const a = escolha[i.id] || i.sugestao;
    r[a].n += 1;
    r[a].valor += Number(i.valor_estimado) || 0;
  }
  return r;
}

/** Só o que muda alguma coisa vai para o servidor ("desligado" = deixar como está). */
export function acoesParaAplicar(itens: ItemLimpeza[], escolha: Record<number, AcaoLimpeza>) {
  return itens.map(i => ({ proposta_id: i.id, acao: escolha[i.id] || i.sugestao }))
    .filter(a => a.acao !== 'desligado');
}
